<template>
  <div class="Text-Responses">
    <h3
      v-if="responses.title && responses.title.trim()"
      class="text-responses-title"
      v-html="responses.title"
    />
    <h5
      v-if="responses.subtitle && responses.subtitle.trim() && responses.type !== 'filter'"
      class="text-responses-subtitle"
    >
      {{ responses.subtitle }}
    </h5>

    <h5
      v-if="responses.type === 'filter' && keywords.length > 0"
      style="text-transform: none; font-size: 16px; text-align: center; padding: 0 10px;"
    >
      <span v-if="responses.originalTotalResponses">
        {{ responses.totalResponses }} responses filtered from {{ responses.originalTotalResponses }} total
      </span>
      <span v-else>
        {{ responses.totalResponses }} filtered responses
      </span>
    </h5>

    <h5
      v-if="responses.notes && responses.notes.trim()"
      style="text-transform: none; text-align: center; padding: 0 10px;"
    >
      {{ responses.notes }}
    </h5>
    <h5
      v-if="responses.type === 'filter' && keywords.length > 0"
      style="text-transform: none; text-align: center; padding: 0 10px;"
      v-html="keywordsTitle"
    />

    <div v-if="!hasGroups && editable" class="response-group-title response-group-title-right-align">
      <button
        class="toggle-responses-button"
        @click="toggleAllCheckboxes"
      >
        Select {{ allChecked ? 'None' : 'All' }}
      </button>
    </div>
    <template v-if="!lazy || load">
      <ResponsesTemplate
        v-for="(resp, index) in responses.answers"
        ref="respTemplates"
        :key="index"
        :lazy="lazy"
        :load="load"
        :dynamic="dynamic"
        :notable="notable"
        :resp="resp"
        :editable="editable"
        :keywords="keywords"
        :index="index"
        :group="responses.answers"
        :toggle-all="allChecked"
        :active-chart="activeChart"
        :sentiment-analysis-mode="sentimentAnalysisMode"
        @checkboxClick="checkboxClick"
        @toggleAllCheckboxes="$emit('toggleAllCheckboxes', $event)"
      />
    </template>
    <template v-else>
      <ResponsesTemplate
        v-for="(resp, index) in responses.answers.slice(0, 10)"
        ref="respTemplates"
        :key="index"
        :lazy="lazy"
        :load="load"
        :dynamic="dynamic"
        :notable="notable"
        :resp="resp"
        :editable="editable"
        :keywords="keywords"
        :index="index"
        :group="responses.answers"
        :toggle-all="allChecked"
        :active-chart="activeChart"
        :sentiment-analysis-mode="sentimentAnalysisMode"
        @checkboxClick="checkboxClick"
        @toggleAllCheckboxes="$emit('toggleAllCheckboxes', $event)"
      />
    </template>
    <div v-if="numAnswers > 10 && lazy && !load" class="text-action-bar">
      <span class="expand-text" @click="loadResponses">View More ></span>
    </div>
  </div>
</template>

<script>
import A from '@/utilities/alpha'
import ResponsesTemplate from './ResponsesTemplate.vue'
import ShiftClick from '../mixins/shift-click.js'
import { getResponseIDs } from '@/utilities/text-response-helpers.js'
import analytics from '@/analytics'

const CONTEXTS = ['shared-report', 'test-details']

export default {
  components: {
    ResponsesTemplate
  },
  mixins: [ShiftClick],
  props: {
    responses: {
      type: Object,
      required: true
    },
    context: {
      type: String,
      required: true,
      validator: (val) => CONTEXTS.includes(val)
    },
    drop: {
      type: [Object, Boolean],
      default: false
    },
    editable: {
      type: Boolean,
      default: false
    },
    toggleAll: {
      type: Boolean,
      default: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    keywords: {
      type: [Array, Boolean],
      default: false
    },
    dynamic: {
      type: Boolean,
      default: false
    },
    notable: {
      type: Boolean,
      default: false
    },
    activeChart: {
      type: String,
      default: 'original'
    },
    sentimentAnalysisMode: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      load: false,
      lastChecked: false,
      allChecked: true,
      filteredCount: 0
    }
  },
  computed: {
    keywordsTitle () {
      if (this.responses.logicOperator === 'AND') {
        return 'Filtered for responses that include: <b>' + this.keywords.join(', ') + '</b>'
      } else if (this.responses.logicOperator === 'OR') {
        return 'Filtered for responses that include at least one: <b>' + this.keywords.join(', ') + '</b>'
      } else if (this.responses.logicOperator === 'NOT') {
        return 'Filtered for responses that do not include: <b>' + this.keywords.join(', ') + '</b>'
      }
      return undefined
    },
    numAnswers () {
      var numAnswers = 0

      if (!this.responses.answers || !this.responses.answers.length) {
        return numAnswers
      }

      function walkAnswers (answers) {
        var firstAnswer = answers[0]
        if (!firstAnswer) {
          return
        }
        if (firstAnswer.type === 'answer') {
          numAnswers = numAnswers + answers.length
        } else {
          answers.forEach(function (answerGroup) {
            walkAnswers(answerGroup.children)
          })
        }
      }

      walkAnswers(this.responses.answers)
      return numAnswers
    },
    hasGroups () {
      return this.responses.answers[0] ? this.responses.answers[0].type === 'group' : false
    }
  },
  watch: {
    toggleAll () {
      // seems stupid, but this is to get each group to maintain its own state
      this.allChecked = this.toggleAll
    }
  },
  methods: {
    shouldShowGroup (group) {
      var bins = Object.keys(group)
      if (bins.length === 0) {
        return false
      }
      var hasGoodBin = false
      bins.forEach(function (bin) {
        if (group[bin].length > 0) {
          hasGoodBin = true
        }
      })
      return hasGoodBin
    },
    loadResponses () {
      this.load = true
      if (this.context === 'shared-report') {
        analytics.track('Shared Report View More', {
          testId: this.drop.id,
          testType: this.drop.payload.testType
        })
      }
    },
    toggleAllCheckboxes (resp) {
      this.allChecked = !this.allChecked
      const clonedResponses = A.clone(this.responses)
      const key = Object.keys(clonedResponses).includes('answers') ? 'answers' : 'children'
      clonedResponses[key] = A.clone(this.responses.answers)
      this.$emit('toggleAllCheckboxes', { toggleAll: this.allChecked, rids: getResponseIDs(clonedResponses), slice: 'original' })
    }
  }
}
</script>
<style scoped lang="scss">
  .Text-Responses {
    cursor: default;
    max-height: 400px;
    overflow-y: auto;
    width: calc(100% - 2px);
    margin-left: 1px;
  }

  .text-responses-title {
    text-align: left;
    font-size: $body-lg;
    font-weight: 400;
    line-height: 1.3;
    padding: $spacing-sm $spacing-md;

    ::v-deep em,
    ::v-deep strong {
      font-weight: 400;
      font-style: normal;
    }
  }

  .text-responses-subtitle {
    font-weight: 500;
    text-transform: none;
    font-size: $body-md;
    text-align: center;
    padding: 0 $spacing-sm $spacing-md;
  }

  .response-group-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #eaeaea;
    font-size: 16px;
    padding: 6px 14px;
    font-weight: bold;
  }

  .toggle-responses-button {
    color: $primary;
    background: transparent;
    padding: 0;
    border: 0;
    font-size: 12px;
    margin-top: 1px;
  }

  .text-action-bar {
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: 10px;
  }

  .text-action-bar .expand-text {
    cursor: pointer;
    color: $primary;
  }
</style>
